/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-22 13:19:16
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import axios from "axios";
import type { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig } from "axios";
import { EventHandler } from "@rfice/eventhandler";
import { Properties } from "../properties";
import { Configure } from "../utilities";
import RestAPIError from "./restapierror";
import ReturnData from "./returndata";

const LOGTAG = "#BaseServiceV2#";

const TIMEOUT = 4 * 1000 * 60;
const DefaultRequestHeaders = {
	Accept: "application/json",
	"Content-Type": "application/json; charset=UTF-8"
};

interface IServiceStatic {
	set_event(events: API.Events): void;
}

export default class BaseServiceV2 implements IServiceStatic {
	private _events: API.Events | null = null;

	constructor(
		private _instance: AxiosInstance | null = null,
		private _header: Partial<{ Accept: string; "Content-Type": string }> = DefaultRequestHeaders,
		private _timeout: number = TIMEOUT
	) {
		this.initialize();
	}

	public set_event(events: API.Events) {
		this._events = events;
	}

	public get header() {
		return this._header;
	}
	public set header(value: Partial<{ Accept: string; "Content-Type": string }>) {
		this._header = value;
	}

	public get instance() {
		try {
			return this._instance!;
		} catch (error: any) {
			throw new Error("BaseService instance is null.", error);
		}
	}

	public static errorHandler: API.ErrorHandler | null = null;

	protected async initialize(args?: API.initializeArgs): Promise<boolean> {
		try {
			this._instance = axios.create({
				headers: {
					...this.header,
					...args?.options
				},
				timeout: args?.timeout || TIMEOUT
			});

			const handleBeforeRequest = (config: InternalAxiosRequestConfig) => {
				args?.onBeforeRequest?.(config);
				return this.beforeRequest(config);
			};

			const handleRequestError = (error: AxiosError) => {
				args?.onRequestError?.(error);
				return this.onRequestError(error);
			};

			const handleAfterResponse = (response: AxiosResponse) => {
				args?.onAfterResponse?.(response);
				return this.afterResponse(response);
			};

			const handleResponseError = (error: AxiosError) => {
				args?.onResponseError?.(error);
				return this.onResponseError(error);
			};

			this.instance.interceptors.request.use(handleBeforeRequest, handleRequestError);
			this.instance.interceptors.response.use(handleAfterResponse, handleResponseError);
			return Promise.resolve(false);
		} catch (error: any) {
			return Promise.reject(error);
		}
	}

	private async beforeRequest(config: InternalAxiosRequestConfig) {
		config.baseURL = Properties.property.apiDomain;
		// config.timeout = this._timeout;
		/*
		const token = Configure.authorizationKey;
		if (token && config.headers) {
			// @ts-ignore
			config.headers["Authorization"] = token;
		}
		/*/
		config.withCredentials = true;
		/**/

		Configure.useLogLevel === 7 && console.debug(LOGTAG, "Request", config.baseURL, config.url);
		return config;
	}

	private afterResponse(response: AxiosResponse) {
		const { config, status, data } = response;
		Configure.useLogLevel === 7 && console.debug(LOGTAG, "Response", config.baseURL, status, data);
		return response;
	}

	private onRequestError(error: AxiosError) {
		return Promise.reject(error);
	}

	protected onResponseError(error: AxiosError) {
		BaseServiceV2.errorHandler?.(error);
		return this.handleReissueCheck(error);
	}

	private navigate_to_logout() {
		Configure.clear_authenticated();
		window.location.replace("/");
	}

	public async reissue_token(originalRequest: InternalAxiosRequestConfig<any> | undefined) {
		return fetch(Properties.property.apiDomain + "/api/reissue_token", {
			method: "POST",
			credentials: "include",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify({
				refreshToken: Configure.refreshToken
			})
		})
			.then((response) => {
				switch (response.status) {
					case 200: {
						return response.json();
					}
					default: {
						this.navigate_to_logout();
						return;
					}
				}
			})
			.then(({ accessToken, refreshToken }) => {
				if (!accessToken && !refreshToken) {
					return;
				}
				Configure.authorizationKey = accessToken;
				Configure.refreshToken = refreshToken;
				if (!originalRequest) return;
				originalRequest.headers.authorization = accessToken;
				return this.instance(originalRequest);
			})
			.catch((error: unknown) => {
				console.warn(LOGTAG, "token refresh failed", error);
				this.navigate_to_logout();
				return error;
			});
	}

	private async handleReissueCheck(error: AxiosError) {
		const originalRequest = error.config;
		if (error.response && error.response.status === 401) {
			console.debug(
				LOGTAG,
				"reissue token:",
				error.response.status,
				"|",
				error.response.config.url,
				"|",
				Configure.refreshToken
			);
		}
		if (
			error.response?.status === 401 &&
			error.response?.config.url !== "/api/reissue_token" &&
			Configure.refreshToken
		) {
			return this.reissue_token(originalRequest);
		}
		return Promise.reject(error);
	}

	private send_success<T>(name: string, data: ReturnData<T>) {
		this._events?.onSuccess?.(name, data);
		EventHandler.emit(`API-${name}-Okay`, data);
	}

	private send_loading(name: string) {
		this._events?.onLoading?.(name);
		EventHandler.emit(`API-${name}-Loading`);
	}

	private send_error(name: string, error: any) {
		this._events?.onError?.(name, error);
		EventHandler.emit(`API-${name}-Error`, error);
	}

	protected async fetch<T = any>(name: string, url: string, config: object) {
		try {
			this.send_loading(name);
			const result = await this.instance.request<ReturnDataType, AxiosResponse<ReturnDataType, T>>({
				url,
				...config
			});
			const data = new ReturnData<T>(result);
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async get<T = any>(
		name: string,
		url: string,
		params = {},
		config?: AxiosRequestConfig
	): Promise<ReturnData<T>> {
		try {
			this.send_loading(name);
			const result = await this.instance.get<ReturnDataType, AxiosResponse<ReturnDataType, T>>(url, {
				params,
				...config
			});
			const data = new ReturnData<T>(result);
			data.parameter = params;
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async post<T = any>(name: string, url: string, payload = {}, config?: AxiosRequestConfig<any>) {
		try {
			this.send_loading(name);
			const result = await this.instance.post<ReturnDataType, AxiosResponse<ReturnDataType, T>>(
				url,
				payload,
				config
			);
			const data = new ReturnData<T>(result);
			data.parameter = payload;
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async put<T = any>(name: string, url: string, payload = {}, config?: AxiosRequestConfig<any>) {
		try {
			this.send_loading(name);
			const result = await this.instance.put<ReturnDataType, AxiosResponse<ReturnDataType, T>>(
				url,
				payload,
				config
			);
			const data = new ReturnData<T>(result);
			data.parameter = payload;
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async patch<T = any>(name: string, url: string, payload = {}, config?: AxiosRequestConfig<any>) {
		try {
			this.send_loading(name);
			const result = await this.instance.patch<ReturnDataType, AxiosResponse<ReturnDataType, T>>(
				url,
				payload,
				config
			);
			const data = new ReturnData<T>(result);
			data.parameter = payload;
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async delete<T = any>(name: string, url: string, payload = {}, config?: AxiosRequestConfig<any>) {
		try {
			this.send_loading(name);
			const result = await this.instance.delete<ReturnDataType, AxiosResponse<ReturnDataType, T>>(url, {
				...(config ? config : {}),
				...(config && config.data ? {} : { data: payload })
			});
			const data = new ReturnData<T>(result);
			data.parameter = payload;
			if (!data.isSuccess) {
				this.send_error(name, data);
				throw new RestAPIError(data);
			}
			this.send_success<T>(name, data);
			return data;
		} catch (error: any) {
			this.send_error(name, error);
			throw new RestAPIError(error);
		}
	}

	protected async upload<T = any>(
		url: string,
		payload = {},
		config: AxiosRequestConfig<any> = {
			headers: { "Content-Type": "multipart/form-data" }
		}
	) {
		try {
			return await this.instance.post<ReturnDataType, AxiosResponse<ReturnDataType, T>>(url, payload, config);
		} catch (error: any) {
			throw new RestAPIError(error);
		}
	}
}
